import type { OpensearchBooleanQuery } from "@redotech/opensearch-types/types";
import {
  type ArrayStringFilterData,
  ArrayStringFilterOperator,
} from "@redotech/redo-model/views/advanced-filters/array-string-filter";
import { DateFilterOperator } from "@redotech/redo-model/views/advanced-filters/date-filter";
import { ExistsFilterOperator } from "@redotech/redo-model/views/advanced-filters/exists-filter";
import {
  AdvancedFilterType,
  FilterOperator,
  isFilterGroup,
} from "@redotech/redo-model/views/advanced-filters/generic-advanced-filter-data";
import { NumberFilterOperator } from "@redotech/redo-model/views/advanced-filters/number-filter";
import { getFilterBuilder } from "@redotech/redo-model/views/utils/util";
import type {
  AdvancedFilterData,
  FilterGroup,
} from "@redotech/redo-model/views/views";
import { AccountId } from "../ids/definitions";

/**
 * Array of all conversation filter names.
 * This is the source of truth for filter names.
 */
export const conversationFilterNames = [
  "status",
  "assignees",
  "channels",
  "conversationTags",
  "customerTags",
  "mentions",
  "readAt",
  "words",
  "createdAt",
  "closedAt",
  "lastResponseAt",
  "lastCustomerMessageAt",
  "lastMerchantMessageAt",
  "customerName",
  "numDrafts",
  "numSentMessages",
] as const;

/**
 * Type representing a valid conversation filter name.
 */
export type ConversationFilterName = (typeof conversationFilterNames)[number];

/**
 * Constant object mapping SCREAMING_CASE names to filter name values.
 * Provides backwards compatibility with the previous enum-style API.
 */
export const ConversationFilterNames = {
  STATUS: "status",
  ASSIGNEES: "assignees",
  CHANNELS: "channels",
  CONVERSATION_TAGS: "conversationTags",
  CUSTOMER_TAGS: "customerTags",
  MENTIONS: "mentions",
  READ_AT: "readAt",
  WORDS: "words",
  CREATED_AT: "createdAt",
  CLOSED_AT: "closedAt",
  LAST_RESPONSE_AT: "lastResponseAt",
  LAST_CUSTOMER_MESSAGE_AT: "lastCustomerMessageAt",
  LAST_MERCHANT_MESSAGE_AT: "lastMerchantMessageAt",
  CUSTOMER_NAME: "customerName",
  NUM_DRAFTS: "numDrafts",
  NUM_SENT_MESSAGES: "numSentMessages",
} as const satisfies Record<string, ConversationFilterName>;
export type ConversationFilterNames =
  (typeof ConversationFilterNames)[keyof typeof ConversationFilterNames];

/**
 * Default filter data for each conversation filter.
 */
export const conversationFilterData = {
  [ConversationFilterNames.STATUS]: {
    name: ConversationFilterNames.STATUS,
    type: AdvancedFilterType.ARRAY_TO_STRING,
    value: [],
    operator: ArrayStringFilterOperator.ANY_OF,
  },
  [ConversationFilterNames.ASSIGNEES]: {
    name: ConversationFilterNames.ASSIGNEES,
    type: AdvancedFilterType.ARRAY_TO_STRING,
    value: [],
    operator: ArrayStringFilterOperator.ANY_OF,
  },
  [ConversationFilterNames.CHANNELS]: {
    name: ConversationFilterNames.CHANNELS,
    type: AdvancedFilterType.ARRAY_TO_STRING,
    value: [],
    operator: ArrayStringFilterOperator.ANY_OF,
  },
  [ConversationFilterNames.CONVERSATION_TAGS]: {
    name: ConversationFilterNames.CONVERSATION_TAGS,
    type: AdvancedFilterType.ARRAY_TO_STRING,
    value: [],
    operator: ArrayStringFilterOperator.ANY_OF,
  },
  [ConversationFilterNames.CUSTOMER_TAGS]: {
    name: ConversationFilterNames.CUSTOMER_TAGS,
    type: AdvancedFilterType.ARRAY_TO_STRING,
    value: [],
    operator: ArrayStringFilterOperator.ANY_OF,
  },
  [ConversationFilterNames.MENTIONS]: {
    name: ConversationFilterNames.MENTIONS,
    type: AdvancedFilterType.ARRAY_TO_STRING,
    value: [],
    operator: ArrayStringFilterOperator.ANY_OF,
  },
  [ConversationFilterNames.READ_AT]: {
    name: ConversationFilterNames.READ_AT,
    type: AdvancedFilterType.EXISTS,
    value: null,
    operator: ExistsFilterOperator.NOT_EXISTS,
  },
  [ConversationFilterNames.WORDS]: {
    name: ConversationFilterNames.WORDS,
    type: AdvancedFilterType.ARRAY_TO_STRING,
    value: [],
    operator: ArrayStringFilterOperator.CONTAINS,
  },
  [ConversationFilterNames.CREATED_AT]: {
    name: ConversationFilterNames.CREATED_AT,
    type: AdvancedFilterType.DATE,
    value: null,
    operator: DateFilterOperator.WITHIN,
  },
  [ConversationFilterNames.CLOSED_AT]: {
    name: ConversationFilterNames.CLOSED_AT,
    type: AdvancedFilterType.DATE,
    value: null,
    operator: DateFilterOperator.WITHIN,
  },
  [ConversationFilterNames.LAST_RESPONSE_AT]: {
    name: ConversationFilterNames.LAST_RESPONSE_AT,
    type: AdvancedFilterType.DATE,
    value: null,
    operator: DateFilterOperator.WITHIN,
  },
  [ConversationFilterNames.LAST_CUSTOMER_MESSAGE_AT]: {
    name: ConversationFilterNames.LAST_CUSTOMER_MESSAGE_AT,
    type: AdvancedFilterType.DATE,
    value: null,
    operator: DateFilterOperator.WITHIN,
  },
  [ConversationFilterNames.LAST_MERCHANT_MESSAGE_AT]: {
    name: ConversationFilterNames.LAST_MERCHANT_MESSAGE_AT,
    type: AdvancedFilterType.DATE,
    value: null,
    operator: DateFilterOperator.WITHIN,
  },
  [ConversationFilterNames.CUSTOMER_NAME]: {
    name: ConversationFilterNames.CUSTOMER_NAME,
    type: AdvancedFilterType.ARRAY_TO_STRING,
    value: [],
    operator: ArrayStringFilterOperator.CONTAINS,
  },
  [ConversationFilterNames.NUM_DRAFTS]: {
    name: ConversationFilterNames.NUM_DRAFTS,
    type: AdvancedFilterType.NUMBER,
    value: [0],
    operator: NumberFilterOperator.GREATER_THAN,
  },
  [ConversationFilterNames.NUM_SENT_MESSAGES]: {
    name: ConversationFilterNames.NUM_SENT_MESSAGES,
    type: AdvancedFilterType.NUMBER,
    value: [0],
    operator: NumberFilterOperator.GREATER_THAN,
  },
} satisfies Record<ConversationFilterName, AdvancedFilterData>;

/**
 * OpenSearch path mapping for a conversation filter.
 */
export interface ConversationFilterPath {
  opensearchPath: string;
  keywordPath?: string;
}

/**
 * Record mapping filter names to their OpenSearch paths.
 */
const conversationFilterPathRecord: Record<
  ConversationFilterName,
  ConversationFilterPath
> = {
  status: { opensearchPath: "status", keywordPath: "status" },
  assignees: { opensearchPath: "assigneeId", keywordPath: "assigneeId" },
  channels: { opensearchPath: "channels", keywordPath: "channels" },
  conversationTags: { opensearchPath: "tags.id", keywordPath: "tags.id" },
  customerTags: {
    opensearchPath: "customerTags",
    keywordPath: "customerTags.keyword",
  },
  mentions: { opensearchPath: "mentions", keywordPath: "mentions" },
  readAt: { opensearchPath: "readAt" },
  words: { opensearchPath: "summary", keywordPath: "summary.keyword" },
  createdAt: { opensearchPath: "createdAt" },
  closedAt: { opensearchPath: "closedAt" },
  lastResponseAt: { opensearchPath: "lastResponseAt" },
  lastCustomerMessageAt: { opensearchPath: "lastCustomerMessageAt" },
  lastMerchantMessageAt: { opensearchPath: "lastMerchantMessageAt" },
  customerName: {
    opensearchPath: "customerName",
    keywordPath: "customerName.keyword",
  },
  numDrafts: { opensearchPath: "numDrafts" },
  numSentMessages: { opensearchPath: "numSentMessages" },
};

/**
 * Gets the OpenSearch path mapping for a conversation filter.
 */
export function getConversationFilterPath(
  name: ConversationFilterName,
): ConversationFilterPath {
  return conversationFilterPathRecord[name];
}

/**
 * Gets the default filter data for a conversation filter name.
 * Returns undefined if the name is not a valid conversation filter.
 */
export function getConversationFilterData(
  name: string,
): AdvancedFilterData | undefined {
  const filterName = conversationFilterNames.find((n) => n === name);
  if (!filterName) {
    return undefined;
  }
  return conversationFilterData[filterName];
}

function createEmptyBoolQuery(): OpensearchBooleanQuery<string> {
  return { filter: [], must: [], must_not: [], should: [] };
}

function isArrayStringFilter(
  filter: AdvancedFilterData,
): filter is ArrayStringFilterData {
  return filter.type === AdvancedFilterType.ARRAY_TO_STRING;
}

const wordsSearchFields = [
  "summary",
  "messageContent",
  "emailSubject",
  "customerName",
  "customerEmail",
  "customerPhone",
  "socialHandles",
];

function isIntegerString(value: string): boolean {
  return /^\d+$/.test(value);
}

function addWordsFilter(
  filter: ArrayStringFilterData,
  compound: OpensearchBooleanQuery<string>,
): void {
  const searchTerm = (filter.value?.[0] as string | undefined)
    ?.substring(0, 200)
    .trim();
  if (!searchTerm) {
    return;
  }

  const should: Record<string, unknown>[] = wordsSearchFields.map((f) => ({
    match: { [f]: searchTerm },
  }));

  if (isIntegerString(searchTerm)) {
    should.push({ term: { ticketNumber: Number(searchTerm) } });
  }

  const multiFieldQuery = { bool: { should, minimum_should_match: 1 } };

  if (filter.operator === ArrayStringFilterOperator.NOT_CONTAINS) {
    compound.must_not.push(multiFieldQuery);
  } else {
    compound.filter.push(multiFieldQuery);
  }
}

function addConversationTagsFilter(
  filter: ArrayStringFilterData,
  path: ConversationFilterPath,
  compound: OpensearchBooleanQuery<string>,
): void {
  const tagValues = filter.value ?? [];
  if (tagValues.length === 0) {
    return;
  }

  if (filter.operator === ArrayStringFilterOperator.ALL_OF) {
    // ALL_OF: Every specified tag must be present
    for (const value of tagValues) {
      compound.filter.push({
        nested: {
          path: "tags",
          query: { term: { [path.keywordPath!]: value } },
        },
      });
    }
    return;
  }

  if (filter.operator === ArrayStringFilterOperator.ANY_OF) {
    // ANY_OF: At least one of the specified tags must be present
    compound.filter.push({
      nested: {
        path: "tags",
        query: {
          bool: { filter: [{ terms: { [path.keywordPath!]: tagValues } }] },
        },
      },
    });
    return;
  }

  if (filter.operator === ArrayStringFilterOperator.NONE_OF) {
    // NONE_OF: None of the specified tags should be present
    // The must_not must be at the outer level, wrapping the nested query
    compound.must_not.push({
      nested: {
        path: "tags",
        query: {
          bool: { filter: [{ terms: { [path.keywordPath!]: tagValues } }] },
        },
      },
    });
    return;
  }
}

/**
 * Adds a single filter to an OpenSearch boolean query using the generic filter builder.
 */
export function addConversationFilterToOpensearch(
  filter: AdvancedFilterData,
  compound: OpensearchBooleanQuery<string>,
  options?: { timeZoneId?: string | null },
): void {
  const filterName = conversationFilterNames.find(
    (name) => name === filter.name,
  );
  if (!filterName) {
    return;
  }

  const path = getConversationFilterPath(filterName);

  if (filterName === "words" && isArrayStringFilter(filter)) {
    addWordsFilter(filter, compound);
    return;
  }

  if (filterName === "conversationTags" && isArrayStringFilter(filter)) {
    addConversationTagsFilter(filter, path, compound);
    return;
  }

  getFilterBuilder(filter).addOpensearchFilters({
    filter,
    opensearchPath: path.opensearchPath,
    keywordPath: path.keywordPath,
    compound,
    timeZoneId: options?.timeZoneId ?? null,
  });
}

/**
 * Processes a filter group recursively, adding filters to the compound query.
 * Handles nested AND/OR groups by creating appropriate nested bool queries.
 */
function processFilterGroup(
  group: FilterGroup,
  compound: OpensearchBooleanQuery<string>,
  options?: { timeZoneId?: string | null },
): void {
  if (group.operator === FilterOperator.AND) {
    for (const clause of group.clauses) {
      if (isFilterGroup(clause)) {
        const subCompound = createEmptyBoolQuery();
        processFilterGroup(clause, subCompound, options);
        compound.filter.push({ bool: subCompound });
      } else {
        addConversationFilterToOpensearch(clause, compound, options);
      }
    }
  } else {
    // OR group - use should with minimum_should_match
    compound.minimum_should_match =
      (compound.minimum_should_match ?? 0) === 0
        ? 1
        : compound.minimum_should_match;

    for (const clause of group.clauses) {
      if (isFilterGroup(clause)) {
        const subCompound = createEmptyBoolQuery();
        processFilterGroup(clause, subCompound, options);
        compound.should.push({ bool: subCompound });
      } else {
        const clauseCompound = createEmptyBoolQuery();
        addConversationFilterToOpensearch(clause, clauseCompound, options);
        compound.should.push({ bool: clauseCompound });
      }
    }
  }
}

/**
 * Builds an OpenSearch boolean query from a filter group.
 * Supports nested AND/OR filter groups.
 * By default, excludes soft-deleted conversations.
 */
export function buildConversationOpensearchQuery(
  accountId: AccountId,
  filterGroup: FilterGroup | null | undefined,
  options?: { timeZoneId?: string | null },
): OpensearchBooleanQuery<string> {
  const compound = createEmptyBoolQuery();

  // Always filter by team
  compound.filter.push({ term: { accountId } });

  // Exclude soft-deleted conversations by default
  compound.must_not.push({ exists: { field: "deletedAt" } });

  if (filterGroup) {
    processFilterGroup(filterGroup, compound, options);
  }

  return compound;
}

/**
 * Builds an OpenSearch boolean query from a flat array of filters.
 * All filters are combined with AND logic.
 * By default, excludes soft-deleted conversations.
 */
export function buildConversationOpensearchQueryFromFilters(
  accountId: AccountId,
  filters: AdvancedFilterData[],
  options?: { timeZoneId?: string | null },
): OpensearchBooleanQuery<string> {
  const compound = createEmptyBoolQuery();

  // Always filter by accountId
  compound.filter.push({ term: { accountId } });

  // Exclude soft-deleted conversations by default
  compound.must_not.push({ exists: { field: "deletedAt" } });

  for (const filter of filters) {
    addConversationFilterToOpensearch(filter, compound, options);
  }

  return compound;
}
